<script lang="ts">
	import * as Yup from "yup";
	import { weeCareEnrollmentState } from "../../state/ApplicationState.svelte";
	import {
		useValidations,
		maskNumericValue,
	} from "../../../../../SharedSvelte/Forms.svelte";  
	import {
		FormGroup,
		Label,
		Input,
		InputGroup,
		Alert,
		Card,
		CardHeader,
		CardTitle,
		CardSubtitle,
		CardBody,
		CardText,
	} from "@sveltestrap/sveltestrap";
	import { Loader } from "../../../../../SharedSvelte/GlobalLoader.svelte";
	import {
		baseUrl,
		isValidDate,
	} from "../../../../../SharedSvelte/Common.svelte";
	import Dialog from "../../../../../SharedSvelte/Dialog.svelte";
	import YesNoQuestionWithDetails from "./YesNoQuestionWithDetails.svelte";

	const { validateField, validateForm, getErrors, resetErrors, setError } =
		useValidations();

	let prompt;
	let apiPromise = $state(fetchWeeCareEnrollmentContent());
	let errors = $derived(getErrors());
	let formHasErrors = $state(false);
	let saveError = $state("");

	let motherHealthConcernsQuestion;
	let infantHealthConcernsQuestion;
	let medicationsBeingTakenQuestion;
	let tobaccoAlcoholOrRecreationslDrugsQuestion;
	let pregnancyConcernsQuestion;
	let hasOtherPoliciesQuestion;
	let providerHighRiskQuestion;
	let pastHighRiskQuestion;
	type FormData = {
		firstName: string;
		lastName: string;
		policyHolder: string;
		birthDate: string;
		street: string;
		city: string;
		state: string;
		zipCode: string;
		phoneNumber: string;
		email: string;
		numberOfPreviousPregnancies: number | null;
		motherComplications: boolean | null;
		motherComplicationsDescription: string;
		infantComplications: boolean | null;
		infantComplicationsDescription: string;
		medications: boolean | null;
		medicationsDescription: string;
		tobaccoAndAlcohol: boolean | null;
		tobaccoAndAlcoholDescription: string;
		pregnancyConcerns: boolean | null;
		pregnancyConcernsDescription: string;
		hasOtherPolicies: boolean | null;
		hasOtherPoliciesDescription: string;
		hasDueDate: boolean | null;
		estimatedDueDate: string;
		firstDayOfLastPeriod: string;
		physiciansName: string;
		isReceivingPrenatalCare: boolean | null;
		firstPrenatalCareAppointment: string;
		deliveryHospital: string;
		phoneInterviewAvailability: string;
		contactWithProvidedPhone: boolean | null;
		phoneInterviewNumber: string;
		subscribersNameOrPehpIdNumber: string;
		providerHighRisk: boolean | null;
		providerHighRiskDescription: string;
		pastHighRisk: boolean | null;
		pastHighRiskDescription: string;
	};

	let formData: FormData = $state({
		firstName: "",
		lastName: "",
		policyHolder: "",
		birthDate: "",
		street: "",
		city: "",
		state: "",
		zipCode: "",
		phoneNumber: "",
		email: "",
		numberOfPreviousPregnancies: null,
		motherComplications: null,
		motherComplicationsDescription: "",
		infantComplications: null,
		infantComplicationsDescription: "",
		medications: null,
		medicationsDescription: "",
		tobaccoAndAlcohol: null,
		tobaccoAndAlcoholDescription: "",
		pregnancyConcerns: null,
		pregnancyConcernsDescription: "",
		hasOtherPolicies: null,
		hasOtherPoliciesDescription: "",
		hasDueDate: null,
		estimatedDueDate: "",
		firstDayOfLastPeriod: "",
		physiciansName: "",
		isReceivingPrenatalCare: null,
		firstPrenatalCareAppointment: "",
		deliveryHospital: "",
		phoneInterviewAvailability: "",
		contactWithProvidedPhone: null,
		phoneInterviewNumber: "",
		subscribersNameOrPehpIdNumber: "",
		providerHighRisk:null,
		providerHighRiskDescription: "",
		pastHighRisk: null,
		pastHighRiskDescription: ""
	});

	const today = new Date(
		new Date().getFullYear(),
		new Date().getMonth(),
		new Date().getDate(),
	);
	const minBirthDate = new Date(
		today.getFullYear() - 70,
		today.getMonth(),
		today.getDate(),
	);
	const maxDueDate = new Date(
		today.getFullYear(),
		today.getMonth() + 9,
		today.getDate(),
	);

	const NAME_REGEX =
		/^(?:\p{Script=Latin}\p{M}*|[ .'\-])+(?:\p{Script=Latin}\p{M})*$/u;
	const NAME_OR_ID_REGEX = /^(?:[Mm]\d{10}(?:-\d{2})?|(?:\p{L}+\p{M}*)(?:[ .'\-]\p{L}+\p{M}*)+)$/u;
	const NAME_MATCH_SUGGESTION = "Please enter the name using letters. (Spaces, hyphens, and apostrophes (') are allowed).";
	const FREETEXT_REGEX = /^(?=.{1,200}$)[A-Za-z0-9 ,.:;!?'\"()&-]+$/;
	const FREETEXT_WARNING = "Please remove invalid characters (Letters, numbers and punctuation allowed).";
	const IS_INTEGER = /^\d+$/;
	const IS_PHONE_ALLOWS_BLANK = /^(?:\(?\b([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})\b)?$/;
	const trueFalse = [true, false];

	let hadPriorPregnancies = $derived(
		formData.numberOfPreviousPregnancies !== null &&
			formData.numberOfPreviousPregnancies !== 0,
	);

	const formSchema = Yup.object({
		policyHolder: Yup.string().required("Policy Holder is Required."),
		subscribersNameOrPehpIdNumber: Yup.string()
			.matches(
				NAME_OR_ID_REGEX,
				NAME_MATCH_SUGGESTION +
					" You may also enter the subscribers PEHP ID (Mxxxxxxxxxx).",
			)
			.required("Please enter the subscribers name or PEHP ID."),
		firstName: Yup.string()
			.matches(NAME_REGEX, NAME_MATCH_SUGGESTION)
			.required("Please enter your first name."),
		lastName: Yup.string()
			.matches(NAME_REGEX, NAME_MATCH_SUGGESTION)
			.required("Please enter your last name."),
		birthDate: Yup.date()
			.transform((value, originalVal) => (originalVal === "" ? null : value))
			.nullable()
			.required("Please enter a valid date.")
			.max(new Date(), "Cannot be in the future.")
			.min(minBirthDate, "Birthdate is out of range."),
		street: Yup.string().required("Street is required."),
		city: Yup.string().required("City is required."),
		state: Yup.string().required("State is required."),
		zipCode: Yup.string()
			.required("Zip Code is Required")
			.matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP Code."),
		phoneNumber: Yup.string()
			.required("Phone Number is Required")
			.matches(IS_PHONE_ALLOWS_BLANK, "Invalid Phone."),
		email: Yup.string()
			.email("Please enter a valid email address.")
			.required("Please enter a valid email address."),
		numberOfPreviousPregnancies: Yup.number()
			.min(0, "Please enter a positive number.")
			.max(999999, "Number out of range.")
			.required("Number of previous pregnancies is required.",),
		hasDueDate: Yup.boolean()
			.transform((val, orig) => {
				// force strings to boolean or if empty, undefined
				if (typeof orig === "string") {
					if (orig.trim() === "") return undefined;
					return orig.trim() === "true";
				}
				return val;
			})
			.required("Please make a selection."),
		estimatedDueDate: Yup.date()
			.transform((value, originalVal) => (originalVal === "" ? null : value))
			.nullable()
			.when("hasDueDate", {
				is: true,
				then: (schema) =>
					schema
						.typeError("Please enter a valid date.")
						.required("Please enter a valid date.")
						.max(maxDueDate, "Must be within the next 9 months.")
						.min(today, "Cannot be in the past."),
				otherwise: (schema) => schema.notRequired().strip(),
			}),
		firstDayOfLastPeriod: Yup.date()
			.transform((value, originalVal) => (originalVal === "" ? null : value))
			.nullable()
			.when("hasDueDate", {
				is: false,
				then: (schema) =>
					schema
						.typeError("Please enter a valid date.")
						.required("Please enter a valid date.")
						.max(today, "Cannot be in the future.")
						.min(minBirthDate, "Cannot be prior to birth date."),
				otherwise: (schema) => schema.notRequired().strip(),
			}),
		physiciansName: Yup.string()
			.matches(
				/^((?:\p{Script=Latin}\p{M}*|[ .'\-])+(?:\p{Script=Latin}\p{M})*)*$/u,
				NAME_MATCH_SUGGESTION,
			) // optional field
			.notRequired(),
		isReceivingPrenatalCare: Yup.boolean()
			.transform((val, orig) => {
				// force strings to boolean or if empty, undefined
				if (typeof orig === "string") {
					if (orig.trim() === "") return undefined;
					return orig.trim() === "true";
				}
				return val;
			})
			.required("Please make a selection."),
		firstPrenatalCareAppointment: Yup.date()
			.transform((value, originalVal) => (originalVal === "" ? null : value ))
			.nullable()
			.when("isReceivingPrenatalCare", {
			is: true,
			then: (schema) =>
				schema
					.typeError("Please enter a valid date.")
					.required("Please enter a valid date.")
					.min(minBirthDate, "Cannot be prior to birth date."),
			otherwise: (schema) => schema.notRequired().strip(),
		}),
		deliveryHospital: Yup.string().required("Please select a value."),
		phoneInterviewAvailability: Yup.string()
			.nullable()
			.matches(FREETEXT_REGEX, FREETEXT_WARNING)
			.required("Please indicate your availability for a phone interview."),
		contactWithProvidedPhone: Yup.boolean()
			.transform((val, orig) => {
				// force strings to boolean or if empty, undefined
				if (typeof orig === "string") {
					if (orig.trim() === "") return undefined;
					return orig.trim() === "true";
				}
				return val;
			})
			.required("Please make a selection."),
		phoneInterviewNumber: Yup.string().when("contactWithProvidedPhone", {
			is: false,
			then: (s) =>
				s
					.trim()
					.required("Phone Number is Required.")
					.matches(IS_PHONE_ALLOWS_BLANK, "Invalid Phone"),
			otherwise: (s) => s.notRequired().strip(),
		}),
	});

	// let apiPromise = $state(fetchWeeCareEnrollmentContent());;
	// let prompt;
	// let errors = $derived(getErrors());
	// let formHasErrors = $state(false);
	// let saveError = $state("");

	function clearAllErrors() {
		resetErrors();
		saveError = "";
	}

	function handleChange(fieldName, fieldValue) {
		if (fieldName === "numberOfPreviousPregnancies" && fieldValue === "0") {
			if (!hadPriorPregnancies) {
				formData.infantComplications = null;
				formData.infantComplicationsDescription = "";
			}
		}
		const vars = new Map();
		vars.set(fieldName, fieldValue);
		const data = Object.fromEntries(vars);
		for (const key in data) {
			//console.log(`${key}: ${data[key]}`);
			validateField(formSchema, key, formData);
		}
	}

	function handleHasDueDate(fieldName, fieldValue) {
		if (fieldName === "hasDueDate") {
			if (fieldValue === "true" || fieldValue === true) {
				formData.firstDayOfLastPeriod = "";
			} else if (fieldValue === "false" || fieldValue === false) {
				formData.estimatedDueDate = "";
			}
		}
		handleChange(fieldName, fieldValue);
	}

	function handleIsReceivingPrenatalCare(fieldName, fieldValue) {
		if (fieldName === "isReceivingPrenatalCare") {
			if (fieldValue === "false" || fieldValue === false) {
				formData.firstPrenatalCareAppointment = "";
			}
		}
		handleChange(fieldName, fieldValue);
	}

	function handleContactWithProvidedPhone(fieldName, fieldValue) {
		if (fieldName === "contactWithProvidedPhone") {
			if (fieldValue === "true" || fieldValue === true) {
				formData.phoneInterviewNumber = "";
			}
		}
		handleChange(fieldName, fieldValue);
	}

	function handlePhoneInput(event) {
		const input = event.target;
		formData.phoneNumber = maskNumericValue(
			input,
			event.inputType,
			"(999) 999-9999",
			formData.phoneNumber,
		);
		validateField(formSchema, "phoneNumber", formData);
	}

	function handlePhoneInterviewNumber(event) {
		const input = event.target;
		formData.phoneInterviewNumber = maskNumericValue(
			input,
			event.inputType,
			"(999) 999-9999",
			formData.phoneInterviewNumber,
		);
		validateField(formSchema, "phoneInterviewNumber", formData);
	}

	async function fetchWeeCareEnrollmentContent() {
		Loader.display();

		window.scrollTo(0, 0);

		try {
			const response = await fetch(
				`${baseUrl}Options/WeeCareEnrollment/WeeCareEnrollmentContent`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						__RequestVerificationToken: document.getElementsByName(
							"__RequestVerificationToken",
						)[0].value,
					},
				},
			);
			 
			if (!response.ok) {
				const error = `Server exception: ${response.status} ${response.statusText}`;
				prompt.showMessage(error, "Information");
				throw new Error(error);
			}

			const data = await response.json();
			weeCareEnrollmentState.hospitals =
				data.weeCareEnrollmentDetails.hospitals;
			weeCareEnrollmentState.relationshipTypes =
				data.weeCareEnrollmentDetails.relationshipTypes;
			weeCareEnrollmentState.subscribersNameOrPehpIdNumber = 
				data.weeCareEnrollmentDetails.subscribersNameOrPehpIdNumber;

			formData.subscribersNameOrPehpIdNumber = data.weeCareEnrollmentDetails.subscribersNameOrPehpIdNumber;

			console.log("WeeCare Enrollment data loaded:", data);

			return data; // Resolves the Promise with the fetched data
		} catch (error) {
			console.error("Error fetching wee care enrollment content:", error);
			return Promise.reject(error); // Rejects the Promise with the error
		} finally {
			Loader.hide();
		}
	}

	$effect(() => {
		formHasErrors =
			Object.keys(errors).length ||
			//motherHealthConcernsQuestion.hasErrors() ||
			(hadPriorPregnancies && infantHealthConcernsQuestion.hasErrors()) 
			//medicationsBeingTakenQuestion.hasErrors() ||
			//tobaccoAlcoholOrRecreationslDrugsQuestion.hasErrors() ||
			// pregnancyConcernsQuestion.hasErrors() ||
			// hasOtherPoliciesQuestion.hasErrors() ||
			// providerHighRiskQuestion.hasErrors() ||
			// pastHighRiskQuestion.hasErrors()
				? true
				: false;
	});

	function applyServerErrors(serverErrors) {
		for (const [field, msg] of Object.entries(serverErrors)) {
			setError(field, Array.isArray(msg) ? msg : [String(msg)]);
		}
	}

	async function saveEnrollment() {
		//console.log("validating:", formData)
		await motherHealthConcernsQuestion.validate();
		if (hadPriorPregnancies) await infantHealthConcernsQuestion.validate();
		await medicationsBeingTakenQuestion.validate();
		await tobaccoAlcoholOrRecreationslDrugsQuestion.validate();
		await pregnancyConcernsQuestion.validate();
		await hasOtherPoliciesQuestion.validate();
		await providerHighRiskQuestion.validate();
		await pastHighRiskQuestion.validate();
		await validateForm(formSchema, formData);
		if (formHasErrors) {
			//console.table(errors);
			return;
		}

		Loader.display();
		try {
			const response = await fetch(
				`${baseUrl}Options/WeeCareEnrollment/enroll`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						__RequestVerificationToken: document.getElementsByName(
							"__RequestVerificationToken",
						)[0].value,
					},
					body: JSON.stringify(formData),
				},
			);
			if (response.ok) {
				const json = await response.json();
				if (!json.success) {
					prompt.showMessage(
						"Your enrollment could not be submitted.  Please try again later.  If you continue to experience difficulties, please contact Wee Care <a href='tel:8013667400'>801-366-7400</a>",
						"Failure",
						() => {
							window.scrollTo(0, 0);
						},
					);
					//throw new Error();
				} else {
					prompt.showMessage(
						"Thank you for enrolling in the Wee Care Program.  You should expect a call or e-mail within 5 business days.<br />If we haven't reached out to you by then, please contact Wee Care at <a href='tel:8013667400'>801-366-7400</a>",
						"Success",
						() => {
							clearAllErrors();
							window.scrollTo(0, 0);
						},
					);
				}
			}

			if (!response.ok) {
				const contentType = response.headers.get("content-type") || "";
				if (contentType.includes("application/json")) {
					const data = await response.json();

					if (data && data.errors) {
						applyServerErrors(data.errors);
						return;
					}
				}
				const error = `Server exception: ${response.status} ${response.statusText}`;
				throw new Error();
			}
		} catch (err) {
			saveError = "Failed to send enrollment. ";
			if (err.message) saveError += err.message;
		} finally {
			Loader.hide();
		}
	}

	function handleSubmit(event) {
		event.preventDefault();
		saveEnrollment();
	}
</script>

<main>
	{#await apiPromise}
		<div>Loading...</div>
	{:then data}
		<form onsubmit={handleSubmit} novalidate>
			<div class="row ms-0 me-0">
				<Card class="mb-4">
					<CardHeader class="wee-title">
						<CardTitle>Relation to Policy Holder</CardTitle>
					</CardHeader>
					<CardBody>
						<CardText>
							<p>
								<FormGroup
									floating
									spacing="mb-3"
									label="Relation to Policy Holder"
								>
									<Input
										type="select"
										bind:value={formData.policyHolder}
										invalid={Boolean(errors.policyHolder)}
										feedback={errors.policyHolder}
										onchange={(e) =>
											handleChange("policyHolder", e.target.value)}
									>
										{#each weeCareEnrollmentState.relationshipTypes as relationshipType}
											<option value={relationshipType.value}
												>{relationshipType.text}</option
											>
										{/each}
									</Input>
								</FormGroup>
							</p>
						</CardText>
					</CardBody>
				</Card>

				<Card class="mb-4">
					<CardHeader  class="wee-title">
						<CardTitle>Name and Personal</CardTitle>
					</CardHeader>
					<CardBody>
						<CardText>
							<p>
								<FormGroup
									floating
									spacing="mb-3"
									label="Subscribers (policy holders) Name Or PEHP ID"
								>
									<Input
										type="text"
										bind:value={formData.subscribersNameOrPehpIdNumber}
										invalid={Boolean(errors.subscribersNameOrPehpIdNumber)}
										feedback={errors.subscribersNameOrPehpIdNumber}
										onchange={(e) =>
											handleChange(
												"subscribersNameOrPehpIdNumber",
												e.target.value,
											)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="First Name">
									<Input
										type="text"
										bind:value={formData.firstName}
										invalid={Boolean(errors.firstName)}
										feedback={errors.firstName}
										on:input={(e) => handleChange("firstName", e.target.value)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="Last Name">
									<Input
										type="text"
										bind:value={formData.lastName}
										invalid={Boolean(errors.lastName)}
										feedback={errors.lastName}
										on:input={(e) => handleChange("lastName", e.target.value)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="Birth Date">
									<Input
										type="date"
										bind:value={formData.birthDate}
										invalid={Boolean(errors.birthDate)}
										feedback={errors.birthDate}
										on:input={(e) => handleChange("birthDate", e.target.value)}
									/>
								</FormGroup>
							</p>
						</CardText>
					</CardBody>
				</Card>

				<Card class="mb-4">
					<CardHeader  class="wee-title">
						<CardTitle>Address and Contact Info</CardTitle>
					</CardHeader>
					<CardBody>
						<CardText>
							<p>
								<FormGroup floating spacing="mb-3" label="Street">
									<Input
										type="text"
										bind:value={formData.street}
										invalid={Boolean(errors.street)}
										feedback={errors.street}
										on:input={(e) => handleChange("street", e.target.value)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="City">
									<Input
										type="text"
										bind:value={formData.city}
										invalid={Boolean(errors.city)}
										feedback={errors.city}
										on:input={(e) => handleChange("city", e.target.value)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="State">
									<Input
										type="text"
										bind:value={formData.state}
										invalid={Boolean(errors.state)}
										feedback={errors.state}
										on:input={(e) => handleChange("state", e.target.value)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="Zip Code">
									<Input
										type="text"
										bind:value={formData.zipCode}
										invalid={Boolean(errors.zipCode)}
										feedback={errors.zipCode}
										on:input={(e) => handleChange("zipCode", e.target.value)}
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="Phone Number">
									<Input
										placeholder="Phone Number"
										type="text"
										bind:value={formData.phoneNumber}
										on:input={handlePhoneInput}
										invalid={Boolean(errors.phoneNumber)}
										feedback={errors.phoneNumber}
										inputmode="numeric"
										maxlength="14"
									/>
								</FormGroup>
								<FormGroup floating spacing="mb-3" label="Email">
									<Input
										type="text"
										bind:value={formData.email}
										invalid={Boolean(errors.email)}
										feedback={errors.email}
										on:blur={(e) => handleChange("email", e.target.value)}
									/>
								</FormGroup>
							</p>
						</CardText>
					</CardBody>
				</Card>

				<Card class="mb-4">
					<CardHeader  class="wee-title">
						<CardTitle>Questionnaire</CardTitle>
					</CardHeader>
					<CardBody>
						<CardText>
							<FormGroup
								floating
								spacing="mb-3"
								label="Number of Previous Pregnancies (including miscarriages and abortions)"
							>
								<Input
									type="number"
									min="0"
									max="999999"
									bind:value={formData.numberOfPreviousPregnancies}
									invalid={Boolean(errors.numberOfPreviousPregnancies)}
									feedback={errors.numberOfPreviousPregnancies}
									on:change={(e) =>
										handleChange("numberOfPreviousPregnancies", e.target.value)}
									inputmode="numeric"
								/>
							</FormGroup>

							<YesNoQuestionWithDetails
								bind:this={motherHealthConcernsQuestion}
								name="motherComplications"
								label="Please describe ongoing health concerns"
								uiText="Do you have ongoing health concerns?"
								bind:yesNoSelection={formData.motherComplications}
								bind:description={formData.motherComplicationsDescription}
							/>
							<!-- <pre>Is null: {formData.numberOfPreviousPregnancies !== null}</pre>
								<pre>{typeof(formData.numberOfPreviousPregnancies)} = {formData.numberOfPreviousPregnancies}</pre> -->
							{#if formData.numberOfPreviousPregnancies !== 0}
								<YesNoQuestionWithDetails
									bind:this={infantHealthConcernsQuestion}
									name="infantComplications"
									label="Complications with Previous Pregnancies"
									uiText="Did you have any problems or complications with your previous pregnancies?"
									bind:yesNoSelection={formData.infantComplications}
									bind:description={formData.infantComplicationsDescription}
								/>
							{/if}

							<YesNoQuestionWithDetails
								bind:this={medicationsBeingTakenQuestion}
								name="medications"
								label="Medications, vitamins, herbs, or supplements"
								uiText="Do you take any medications, including over-the counter medications, prescription medications, vitamins, herbs, or dietary supplements?"
								bind:yesNoSelection={formData.medications}
								bind:description={formData.medicationsDescription}
							/>

							<YesNoQuestionWithDetails
								bind:this={tobaccoAlcoholOrRecreationslDrugsQuestion}
								name="tobaccoAndAlcohol"
								label="Tobacco, Alcohol or Recreational Drugs."
								uiText="Do you use tobacco products, drink alchoholic beverages, or use any recreational drugs?"
								bind:yesNoSelection={formData.tobaccoAndAlcohol}
								bind:description={formData.tobaccoAndAlcoholDescription}
							/>

							<YesNoQuestionWithDetails
								bind:this={providerHighRiskQuestion}
								name="providerHighRiskQuestion"
								label="High-Risk Pregnancy"
								uiText="Has your provider determined that you are currently in a high‑risk pregnancy?"
								bind:yesNoSelection={formData.providerHighRisk}
								bind:description={formData.providerHighRiskDescription}
							/>

							<YesNoQuestionWithDetails
								bind:this={pastHighRiskQuestion}
								name="pastHighRiskQuestion"
								label="Past High-Risk Pregnancy"
								uiText="Have you ever been determined to be high‑risk in a past pregnancy?"
								bind:yesNoSelection={formData.pastHighRisk}
								bind:description={formData.pastHighRiskDescription}
							/>

							<YesNoQuestionWithDetails
								bind:this={pregnancyConcernsQuestion}
								name="pregnancyConcerns"
								label="Please list pregnancy problems or concerns."
								uiText="Are you aware of any problems or concerns with this pregnancy?"
								bind:yesNoSelection={formData.pregnancyConcerns}
								bind:description={formData.pregnancyConcernsDescription}
							/>

							<YesNoQuestionWithDetails
								bind:this={hasOtherPoliciesQuestion}
								name="hasOtherPolicies"
								label="Additional Health Insurance Policies"
								uiText="Do you have additional health insurance policies?"
								bind:yesNoSelection={formData.hasOtherPolicies}
								bind:description={formData.hasOtherPoliciesDescription}
							/>
						</CardText>
					</CardBody>
				</Card>

				<Card class="mb-4">
					<CardHeader class="wee-title">
						<CardTitle>Delivery Information</CardTitle>
					</CardHeader>
					<CardBody>
						<CardText>
							<div class="row">
								<div class="col-6 col-sm-2 col-xl-1">
									{#each trueFalse as value}
										<FormGroup>
											<Input
												type="radio"
												name="hasDueDate"
												id="hasDueDate{value}"
												{value}
												label={value ? "Yes" : "No"}
												bind:group={formData.hasDueDate}
												onchange={(e) =>
													handleHasDueDate("hasDueDate", e.target.value)}
												invalid={Boolean(errors.hasDueDate)}
												feedback={errors.hasDueDate}
											/>
										</FormGroup>
									{/each}
								</div>
								<div class="col-6 col-sm-10 col-xl-11">
									<label for="hasDueDate"
										>Do you know your estimated due date?
									</label>
									{#if Boolean(errors?.hasDueDate)}
										<div id="hasDueDate" class="radioButtonInvalid">
											{errors.hasDueDate}
										</div>
									{/if}
									{#if formData.hasDueDate}
										<FormGroup
											floating
											label="Estimated Due Date"
											class="mt-2 mb-3"
										>
											<Input
												type="date"
												bind:value={formData.estimatedDueDate}
												invalid={Boolean(errors.estimatedDueDate)}
												feedback={errors.estimatedDueDate}
												on:input={(e) =>
													handleChange("estimatedDueDate", e.target.value)}
											/>
										</FormGroup>
									{/if}
									{#if !formData.hasDueDate}
										<FormGroup
											floating
											label="First Day of Last Period"
											class="mt-2 mb-3"
										>
											<Input
												type="date"
												bind:value={formData.firstDayOfLastPeriod}
												invalid={Boolean(errors.firstDayOfLastPeriod)}
												feedback={errors.firstDayOfLastPeriod}
												on:input={(e) =>
													handleChange("firstDayOfLastPeriod", e.target.value)}
											/>
										</FormGroup>
									{/if}
								</div>
							</div>

							<div class="row">
								<div class="col-sm-12">
									<FormGroup floating label="Physician or Midwife's Full Name">
										<Input
											type="text"
											bind:value={formData.physiciansName}
											invalid={Boolean(errors.physiciansName)}
											feedback={errors.physiciansName}
											onchange={(e) =>
												handleChange("physiciansName", e.target.value)}
										/>
									</FormGroup>
								</div>
							</div>

							<div class="row">
								<div class="col-6 col-sm-2 col-xl-1">
									{#each trueFalse as value}
										<FormGroup>
											<Input
												type="radio"
												name="isReceivingPrenatalCare"
												id="isReceivingPrenatalCare{value}"
												{value}
												label={value ? "Yes" : "No"}
												bind:group={formData.isReceivingPrenatalCare}
												on:change={(e) =>
													handleIsReceivingPrenatalCare(
														"isReceivingPrenatalCare",
														e.target.value,
													)}
												invalid={Boolean(errors.isReceivingPrenatalCare)}
												feedback={errors.isReceivingPrenatalCare}
											/>
										</FormGroup>
									{/each}
								</div>
								<div class="col-6 col-sm-10 col-xl-11">
									<label for="isReceivingPrenatalCare"
										>Are you receiving prenatal care?
									</label>
									{#if Boolean(errors?.isReceivingPrenatalCare)}
										<div
											id="isReceivingPrenatalCareErrors"
											class="radioButtonInvalid"
										>
											{errors.isReceivingPrenatalCare}
										</div>
									{/if}
									{#if formData.isReceivingPrenatalCare}
										<FormGroup
											floating
											label="First Prenatal Care Appointment Date"
											class="mt-2 mb-3"
										>
											<Input
												type="date"
												bind:value={formData.firstPrenatalCareAppointment}
												invalid={Boolean(errors.firstPrenatalCareAppointment)}
												feedback={errors.firstPrenatalCareAppointment}
												on:input={(e) =>
													handleChange("firstPrenatalCareAppointment",e.target.value)}
											/>
										</FormGroup>
									{/if}
								</div>
							</div>

							<div class="row">
								<div class="col-sm-12">
									<FormGroup
										floating
										spacing="mb-3"
										label="Anticipated Delivery Hospital"
									>
										<Input
											type="select"
											bind:value={formData.deliveryHospital}
											invalid={Boolean(errors.deliveryHospital)}
											feedback={errors.deliveryHospital}
											on:change={(e) =>
												handleChange("deliveryHospital", e.target.value)}
										>
											{#each weeCareEnrollmentState.hospitals as hospital}
												<option value={hospital.value}>{hospital.text}</option>
											{/each}
										</Input>
									</FormGroup>
								</div>
							</div>
						</CardText>
					</CardBody>
				</Card>

				<Card class="mb-4">
					<CardHeader class="wee-title">
						<CardTitle>Telephone Interview</CardTitle>
					</CardHeader>
					<CardBody>
						<CardText>
							<div class="row">
								<div class="col-sm-12">
									As part of your enrollment in WeeCare, we will schedule a
									brief telephone inteview. The WeeCare program will be
									explained and your questions answered. Interview calls are
									done on Mondays, Wednesdays and Thursdays from 9:00 AM to 4:30
									PM. Please list your availability below so we can schedule
									your appointment. (If there are no times on Monday and
									Wednesday that will work for you, please indicate below, and
									we will try our best to accommodate you at another time.)
									Please indicate what phone number we should use to reach you
									for your telephone interview.
									<div class="col-sm-12">
										<FormGroup floating label="Availability">
											<Input
												type="text"
												bind:value={formData.phoneInterviewAvailability}
												invalid={Boolean(errors.phoneInterviewAvailability)}
												feedback={errors.phoneInterviewAvailability}
												on:input={(e) =>
													handleChange(
														"phoneInterviewAvailability",
														e.target.value,
													)}
											/>
										</FormGroup>
									</div>
									<div></div>
									<div class="row">
										<div class="col-6 col-sm-2 col-xl-1">
											{#each trueFalse as value}
												<FormGroup>
													<Input
														type="radio"
														name="contactWithProvidedPhone"
														id="contactWithProvidedPhone{value}"
														{value}
														label={value ? "Yes" : "No"}
														bind:group={formData.contactWithProvidedPhone}
														on:change={(e) =>
															handleContactWithProvidedPhone(
																"contactWithProvidedPhone",
																e.target.value,
															)}
														invalid={Boolean(errors.contactWithProvidedPhone)}
														feedback={errors.contactWithProvidedPhone}
													/>
												</FormGroup>
											{/each}
										</div>
										<div class="col-6 col-sm-10 col-xl-11">
											<label for="contactWithProvidedPhone"
												>May we contact you with the phone number you provided above?
											</label>
											{#if Boolean(errors?.contactWithProvidedPhone)}
												{errors.contactWithProvidedPhone}
											{/if}
											{#if !formData.contactWithProvidedPhone && formData.contactWithProvidedPhone != undefined}
												<FormGroup
													floating
													label="Contact Phone"
													class="mt-2 mb-3"
												>
													<Input
														placeholder="Phone Number"
														type="text"
														bind:value={formData.phoneInterviewNumber}
														oninput={handlePhoneInterviewNumber}
														invalid={Boolean(errors.phoneInterviewNumber)}
														feedback={errors.phoneInterviewNumber}
														inputmode="numeric"
														maxlength="14"
													/>
												</FormGroup>
											{/if}
										</div>
									</div>
								</div>
							</div></CardText
						>
					</CardBody>
				</Card>

				<div class="mb-5">
					{#if saveError}
						<p class="text-danger" role="alert">{saveError}</p>
					{/if}
						<button
							type="submit"
							class="btn btn-primary btn-lg">Save</button
						>
				</div>
				<!-- 
				<div class="col-sm-5">
					<aside class="sticky-top">
						<pre>{JSON.stringify(formData, null, 2)}</pre>
						<pre class="radioButtonInvalid">
					{JSON.stringify(errors, null, 1)}
				</pre>
					</aside>
				</div> -->
			</div>
		</form>
	{/await}
</main>
<Dialog bind:this={prompt} />

<style>
	.radioButtonInvalid {
		color: var(--bs-form-invalid-color);
		font-size: 0.875em;
		margin-top: 0.25rem;
		width: 100%;
	}
</style>
